import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavigateFunction } from 'react-router-dom'
import { useDownloadFile, useSearchReviews } from '.'
import { searchReviewService } from '../services'
import { notificationService } from '../services/notificationService'
import { RootState } from '../store'
import {
  clearSearchReviewState,
  Document,
  SearchQuery,
  SearchReview,
  setDetailsCsv,
  setSearchQueryLoading,
  setSelectedSearchQuery,
  setSelectedSearchResultItem,
} from '../store/reducers/searchReviewReducer'

interface UseSearchReviewQueriesProps {
  searchReviewId?: string
  searchQueryId?: string
  documentId?: string
  preventFetch?: boolean
}

const useSearchReviewQueries = ({
  searchReviewId,
  searchQueryId,
  documentId,
  preventFetch = false,
}: UseSearchReviewQueriesProps) => {
  const dispatch = useDispatch()
  const downloadCsv = useDownloadFile()

  const {
    selectedSearchReview,
    updateSelectedSearchReview,
    refreshSearchReviewList,
  } = useSearchReviews({
    preventFetch: true,
  })

  const selectedSearchQuery: SearchQuery | undefined = useSelector(
    (state: RootState) => state.searchReview.selectedSearchQuery
  )

  const searchQueryLoading = useSelector(
    (state: RootState) => state.searchReview.searchQueryLoading
  )

  const selectedDocument: Document | undefined = useSelector(
    (state: RootState) => state.searchReview.selectedSearchResultItem
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<AxiosError | Error>()
  const [isFetchDone, setIsFetchDone] = useState(false)

  const fetchSearchReviewQuery = useCallback(
    async (refreshForceCall = false, searchQueryId: string) => {
      if (!searchReviewId || !searchQueryId) return
      if (!loading && ((!preventFetch && !isFetchDone) || refreshForceCall)) {
        try {
          setLoading(true)
          dispatch(setSearchQueryLoading(true))
          const response = await searchReviewService.getSearchReviewQueryById({
            searchReviewId,
            searchQueryId,
          })
          dispatch(setSelectedSearchQuery(response))
          dispatch(setSearchQueryLoading(false))
          if (documentId) {
            const document = response.searchResults.find(
              (result: Document) => result.id === documentId
            )
            if (document) {
              dispatch(setSelectedSearchResultItem(document))
            }
          }
          setIsFetchDone(true)
        } catch (error) {
          console.error('axios get error', error)
          setError(error as AxiosError | Error)
        } finally {
          dispatch(setSearchQueryLoading(false))
          setLoading(false)
        }
      }
    },
    [dispatch, searchReviewId, isFetchDone, loading, preventFetch, documentId]
  )

  const refreshSearchReviewQuery = useCallback(
    (searchQueryId: string) => {
      fetchSearchReviewQuery(true, searchQueryId)
    },
    [fetchSearchReviewQuery]
  )

  useEffect(() => {
    if (searchReviewId) {
      fetchSearchReviewQuery(false, searchQueryId || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchReviewId])

  const clearSearchReviewQuery = useCallback(() => {
    dispatch(clearSearchReviewState())
  }, [dispatch])

  const exportSearchReviewQueryCsv = useCallback(
    (searchReviewId: string) => {
      if (searchReviewId) {
        searchReviewService
          .exportSearchReviewToCsv({ searchReviewId })
          .then((response: string) => {
            const filename = `${selectedSearchQuery?.searchText}.csv`
            downloadCsv(response, filename)
            dispatch(setDetailsCsv(response))
          })
          .catch((error: AxiosError) => {
            console.error('Error fetching search review CSV data', error)
          })
      }
    },
    [dispatch, downloadCsv, selectedSearchQuery?.searchText]
  )

  interface SaveSearchQueryInclusionProps {
    recordIds: string[]
    navigate: NavigateFunction
  }

  const saveSearchQueryInclusion = useCallback(
    ({ recordIds, navigate }: SaveSearchQueryInclusionProps) => {
      return searchReviewService
        .saveSearchQueryInclusion({
          searchReviewId: selectedSearchReview?.id || '',
          includedSearchResults:
            selectedSearchReview?.includedSearchResults || [],
          recordIds,
          navigate,
        })
        .then((response: SearchReview) => {
          updateSelectedSearchReview(response)
          notificationService.notificationSuccess({
            messageToDisplay:
              t('searchReviewSearchDetailsPage.saveDocumentSuccessful') || '',
          })
          refreshSearchReviewList()
          return response
        })
        .catch((error) => {
          console.error('axios fetch error', error)
          return error
        })
    },
    [
      selectedSearchReview,
      updateSelectedSearchReview,
      refreshSearchReviewList,
    ]
  )

  return {
    fetchSearchReviewQuery,
    refreshSearchReviewQuery,
    error,
    selectedSearchQuery,
    clearSearchReviewQuery,
    exportSearchReviewQueryCsv,
    searchQueryLoading,
    saveSearchQueryInclusion,
    selectedDocument,
  }
}

export default useSearchReviewQueries
